/**
 * Financing Partner Banner - Visual Image Banner
 * Shows couple getting approved with property upgrade progression
 * For footer and dashboard placement
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowRight, Landmark, DollarSign, CheckCircle, TrendingUp, Home } from 'lucide-react';
import { Button } from '../ui/button';

const FinancingPartnerBanner = ({ variant = 'footer' }) => {
  const navigate = useNavigate();

  if (variant === 'dashboard') {
    return (
      <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900 rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center">
              <Landmark className="h-8 w-8 text-white" />
            </div>
            <div>
              <p className="text-purple-200 text-sm">Real Estate Financing</p>
              <h3 className="text-xl font-bold">Multiple Financial Institutions</h3>
              <p className="text-purple-200 text-sm">Competitive rates & flexible terms</p>
            </div>
          </div>
          
          <div className="text-center md:text-right">
            <div className="mb-2">
              <span className="text-3xl font-bold text-emerald-400">40%</span>
              <span className="text-purple-200 ml-2">LTV Available</span>
            </div>
            <Button 
              onClick={() => navigate('/financing')}
              className="bg-white text-purple-900 hover:bg-purple-100"
            >
              Apply for Financing
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-xs text-purple-300 text-center">
            Sebastian Alliance Group LLC facilitates introductions to regulated financial institutions.
          </p>
        </div>
      </div>
    );
  }

  // Footer variant (default) - Visual Image Banner
  return (
    <div className="relative bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 py-8 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          
          {/* Left Section - Couple Getting Approved */}
          <div className="lg:col-span-4 relative">
            <div className="relative rounded-xl overflow-hidden shadow-2xl border-2 border-emerald-400/30">
              <img 
                src="https://images.pexels.com/photos/8293771/pexels-photo-8293771.jpeg?auto=compress&cs=tinysrgb&w=600" 
                alt="Financing Approved"
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-bold">Financing Approved!</span>
                </div>
              </div>
            </div>
          </div>

          {/* Center Section - Partner Network Info */}
          <div className="lg:col-span-4 text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="flex justify-center mb-3">
                <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <Landmark className="h-7 w-7 text-emerald-400" />
                </div>
              </div>
              <p className="text-blue-300 text-sm mb-1">Partner Network</p>
              <h3 className="text-white font-bold text-xl mb-2">Multiple Financial Institutions</h3>
              <p className="text-blue-200 text-sm mb-4">Competitive rates & flexible terms</p>
              
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-emerald-400 font-bold text-2xl">40%</p>
                  <p className="text-blue-300 text-xs">Max LTV</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <p className="text-emerald-400 font-bold text-2xl">24hr</p>
                  <p className="text-blue-300 text-xs">Response</p>
                </div>
              </div>

              <Button 
                onClick={() => navigate('/financing')}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 w-full"
              >
                <DollarSign className="h-4 w-4 mr-1" />
                Get Financing
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>

          {/* Right Section - Property Upgrade Progression */}
          <div className="lg:col-span-4">
            <div className="relative">
              {/* Small Home to Apartments Progression */}
              <div className="flex items-center gap-3">
                {/* Small Home */}
                <div className="flex-1 relative rounded-lg overflow-hidden shadow-xl border border-white/20">
                  <img 
                    src="https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=300&q=80" 
                    alt="Small Home"
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-2 left-2">
                    <Home className="h-4 w-4 text-white" />
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                </div>

                {/* Apartment Building */}
                <div className="flex-1 relative rounded-lg overflow-hidden shadow-xl border-2 border-emerald-400/50">
                  <img 
                    src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=300&q=80" 
                    alt="Apartment Building"
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-2 left-2">
                    <Building2 className="h-4 w-4 text-emerald-400" />
                  </div>
                </div>
              </div>
              
              <p className="text-center text-blue-200 text-sm mt-3">
                <span className="text-white font-semibold">Upgrade Your Portfolio</span> — From single homes to multi-unit properties
              </p>
            </div>
          </div>
        </div>
        
        {/* Legal disclaimer */}
        <div className="text-center mt-6 pt-4 border-t border-white/10">
          <p className="text-xs text-blue-300/70">
            Sebastian Alliance Group LLC facilitates introductions to regulated financial institutions in collaboration with licensed real estate professionals.
          </p>
          <p className="text-xs text-blue-300/50 mt-1">
            Financing provided exclusively by third-party banks. Sebastian Alliance Group LLC does not provide lending or financial advisory services.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FinancingPartnerBanner;
